"use client";

import { useState } from "react";
import { cancelMembership, type AccountMembership } from "./account-api";
import { formatAccountDate } from "./account-format";

export function MembershipPanel({
  membership,
  token,
  onCancelled,
}: {
  membership: AccountMembership | null | undefined;
  token: string;
  onCancelled: (membership: AccountMembership) => void;
}) {
  const active = isActiveMembership(membership);
  const canCancel = isCancellableMembership(membership);
  const plan = membershipPlanLabel(membership);
  const renews = membershipRenewalDate(membership);
  const cancelRequested = membership?.status === "cancel_requested";
  const status = membershipStatusLabel(membership, active);
  return (
    <section className="mb-6 rounded-lg border border-[#e8dccd] bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6b7d5c]">
            Membership
          </p>
          <h2 className="mt-1 font-serif text-2xl">
            {active ? "Ishvaram Membership active" : "No active membership"}
          </h2>
          <p className="mt-1 text-sm capitalize text-[#68645d]">
            {active ? plan : "Use membership to unlock ongoing chat access."}
            {renews ? ` - renews ${renews}` : ""}
          </p>
        </div>
        <span
          className={`self-start rounded-full px-3 py-1 text-xs font-semibold ${
            active ? "bg-[#e6efe0] text-[#315f2f]" : "bg-[#f3e8de] text-[#8a4d35]"
          }`}
        >
          {status}
        </span>
      </div>
      <MembershipCancelControl
        canCancel={canCancel}
        cancelRequested={cancelRequested}
        renews={renews}
        token={token}
        onCancelled={onCancelled}
      />
    </section>
  );
}

function isActiveMembership(membership: AccountMembership | null | undefined) {
  return Boolean(membership?.active);
}

function isCancellableMembership(membership: AccountMembership | null | undefined) {
  return Boolean(membership?.active && membership.subscription_id);
}

function membershipPlanLabel(membership: AccountMembership | null | undefined) {
  return membership?.plan_id ? membership.plan_id.replaceAll("_", " ") : "No plan";
}

function membershipRenewalDate(membership: AccountMembership | null | undefined) {
  return membership?.current_period_end ? formatAccountDate(membership.current_period_end) : null;
}

function membershipStatusLabel(membership: AccountMembership | null | undefined, active: boolean) {
  return membership?.status || (active ? "active" : "inactive");
}

function MembershipCancelControl({
  canCancel,
  cancelRequested,
  renews,
  token,
  onCancelled,
}: {
  canCancel: boolean;
  cancelRequested: boolean;
  renews: string | null;
  token: string;
  onCancelled: (membership: AccountMembership) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function handleCancel() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await cancelMembership(token);
      onCancelled(result.membership);
      setConfirming(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not cancel membership");
    }
    setBusy(false);
  }

  if (cancelRequested) return <CancelRequestedNotice renews={renews} />;
  if (!canCancel) return null;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[#efe5d8] pt-4">
      <button
        type="button"
        disabled={busy}
        onClick={handleCancel}
        className="rounded-md border border-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? "Cancelling..." : confirming ? "Confirm cancellation" : "Cancel renewal"}
      </button>
      {confirming ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => setConfirming(false)}
          className="text-sm font-semibold text-[#68645d] disabled:cursor-not-allowed"
        >
          Keep membership
        </button>
      ) : null}
      {error ? <p className="basis-full text-sm text-[#8b3732]">{error}</p> : null}
    </div>
  );
}

function CancelRequestedNotice({ renews }: { renews: string | null }) {
  return (
    <p className="mt-4 border-t border-[#efe5d8] pt-4 text-sm leading-6 text-[#68645d]">
      Renewal cancellation is scheduled. Your paid access remains active until
      {renews ? ` ${renews}` : " the current period ends"}.
    </p>
  );
}
